import { executeContractRequest } from '@/api/contract-client';
import { ApiError, ApiProtocolError } from '@/api/errors';
import type { HtmlDynamicsHistory, HtmlGateway, HtmlParams } from '@/api/html-types';
import type { ApiRequestOptions } from '@/api/provider';
import type { ContractClient } from './index';

// Wire branches are checked before crossing into the HTML-only application model.
function htmlResponse<T>(value: unknown): T {
  if (
    !value ||
    typeof value !== 'object' ||
    !('response_format' in value) ||
    value.response_format !== 'html'
  ) {
    throw new ApiProtocolError('Ожидался HTML-контракт координат. Обновите приложение.');
  }
  assertPositionContext(value);
  return value as T;
}

export function createBackendHtml(client: ContractClient): HtmlGateway {
  return {
    async dynamics(params, options) {
      const value: unknown = await loadHistory(client, params, options);
      if (
        !value ||
        typeof value !== 'object' ||
        !('metric_schema_version' in value) ||
        value.metric_schema_version !== 'dynamics_page_metrics_v1'
      ) {
        throw new ApiProtocolError('Неизвестная схема метрик. Обновите приложение.');
      }
      assertPositionContext(value);
      return value as HtmlDynamicsHistory;
    },
    async targetQueries(params, options) {
      return htmlResponse(
        await executeContractRequest(
          client.GET('/api/v1/analytics/target-query-index', {
            params: { query: params as never },
            signal: options?.signal,
          }),
        ),
      );
    },
    async resultIndex(experimentId, params, options) {
      return htmlResponse(
        await executeContractRequest(
          client.GET('/api/v1/experiments/{experiment_id}/result-index', {
            params: { path: { experiment_id: experimentId }, query: params },
            signal: options?.signal,
          }),
        ),
      );
    },
    async expandedSerp(params, options) {
      const value = await executeContractRequest(
        client.POST('/api/v1/serps/search', { body: params, signal: options?.signal }),
      );
      if (value.items.length !== 1) throw new ApiProtocolError('Снимок недоступен.');
      return htmlResponse(value.items[0]);
    },
    async comparisonQueries(params, options) {
      return htmlResponse(
        await executeContractRequest(
          client.GET('/api/v1/comparison/queries', {
            params: { query: params as never },
            signal: options?.signal,
          }),
        ),
      );
    },
    async comparison(params, options) {
      return htmlResponse(
        await executeContractRequest(
          client.GET('/api/v1/comparison', {
            params: { query: params as never },
            signal: options?.signal,
          }),
        ),
      );
    },
  };
}

function assertPositionContext(value: object): void {
  const context = 'position_context' in value ? value.position_context : null;
  if (
    !context ||
    typeof context !== 'object' ||
    !('rank_model' in context) ||
    context.rank_model !== 'html_component_page_rank_v1' ||
    !('rank_scope' in context) ||
    context.rank_scope !== 'page' ||
    !('counting_unit' in context) ||
    context.counting_unit !== 'component'
  ) {
    throw new ApiProtocolError('Неизвестная модель координат. Обновите приложение.');
  }
}

async function loadHistory(
  client: ContractClient,
  params: HtmlParams,
  options?: ApiRequestOptions,
) {
  const load = () =>
    executeContractRequest(
      client.GET('/api/v1/analytics/dynamics', {
        params: { query: params as never },
        signal: options?.signal,
      }),
    );
  try {
    return await load();
  } catch (error) {
    if (isRevisionRace(error)) return load();
    throw error;
  }
}
function isRevisionRace(error: unknown) {
  if (!(error instanceof ApiError) || error.code !== 'report_context_changed') return false;
  return (
    error.payload &&
    typeof error.payload === 'object' &&
    'reason' in error.payload &&
    error.payload.reason === 'run_data_changed'
  );
}
